/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect } from "react";
import dayjs from "dayjs";
import { When } from "react-if";
import { useParams } from "react-router-dom";
import { useLockBodyScroll } from "react-use";
import {
  BrickLoader,
  FlexContainer,
  Message,
  Sidebar,
  Text,
  theme,
} from "@nordcloud/gnui";
import { LoaderWrap, Pagination, ReactTable } from "~/components";
import { dateFormat } from "~/constants";
import { useQueryState } from "~/hooks";
import { formatMoney, isNotNil } from "~/tools";
import { RouteParams } from "~/views/applications/types";
import { ANOMALIES_TABLE } from "../../components";
import { useAnomaliesDetails } from "../../hooks/useAnomaliesDetails";

type Props = {
  isSidebarOpen: boolean;
  onClose: () => void;
  anomalyCost: string;
  date: string;
};

export function AnomaliesDetails({
  isSidebarOpen,
  onClose,
  anomalyCost,
  date,
}: Props) {
  const { application: appId = "" } = useParams<RouteParams>();

  const {
    state: { limit, page },
    updateQueryState,
  } = useQueryState();

  const { getAnomalyDetails, isAnomaliesLoading, anomalies, anomaliesCount } =
    useAnomaliesDetails({ date });

  useEffect(() => {
    if (isSidebarOpen) {
      getAnomalyDetails({
        variables: {
          limit,
          page,
          applicationId: appId,
          date,
        },
      });
    }
  }, [isSidebarOpen, date, appId, getAnomalyDetails, limit, page]);

  const handleClose = () => {
    updateQueryState({ page: 0 });
    onClose();
  };

  useLockBodyScroll(isSidebarOpen);

  return (
    <Sidebar
      title="Anomaly Cost Details"
      isOpen={isSidebarOpen}
      width="61rem"
      onClick={handleClose}
    >
      <Message status="notification" image="info">
        <FlexContainer direction="column" alignItems="start">
          <Text
            weight="medium"
            color={theme.color.text.info}
            mb={theme.spacing.spacing02}
          >
            Anomaly cost is calculated based on the service level data.
          </Text>
          <Text mb={theme.spacing.spacing00} color={theme.color.text.text02}>
            Summing up all the estate records related to the anomaly will not
            give the same result.
          </Text>
        </FlexContainer>
      </Message>
      <LoaderWrap
        loading={isAnomaliesLoading}
        Component={BrickLoader}
        viewBox="0 0 10 4"
        style={{ height: "auto" }}
      >
        <When condition={isNotNil(anomalyCost)}>
          <FlexContainer
            css={{
              marginTop: theme.spacing.spacing05,
              marginBottom: theme.spacing.spacing05,
              paddingLeft: theme.spacing.spacing01,
            }}
          >
            <FlexContainer
              direction="column"
              rowGap={theme.spacing.spacing02}
              alignItems="start"
            >
              <Text size="sm" tag="span" color={theme.color.text.text02}>
                Anomaly Cost on {dayjs(date).format(dateFormat.dayMonthYear)}
              </Text>
              <Text
                size="lg"
                tag="strong"
                css={{ fontWeight: theme.fontWeights.medium }}
              >
                {formatMoney(anomalyCost)}
              </Text>
            </FlexContainer>
          </FlexContainer>
        </When>
        <ReactTable columns={ANOMALIES_TABLE} data={anomalies} sort={false} />
        <Pagination count={anomaliesCount} />
      </LoaderWrap>
    </Sidebar>
  );
}
